//#region imports
import React, { useEffect, useState } from "react";

// custom components
import { CustomSpinner } from "_atoms";
import JobCard from "./job-card";
import TaskAssignmentTimeWarning from "./task-assignment-time-warning";
import PickupDropOffDropDowns from "./pickup-dropoff-dropdowns";

// bootstrap
import Button from "react-bootstrap/esm/Button";
import { Col, Form, Row } from "react-bootstrap";

// tremor
import { Subtitle } from "@tremor/react";

// redux
import { useSelector, useDispatch } from "react-redux";
import { selectLatestJob, createTask, selectCreateTaskStatus, clearCreateTaskStatus, selectCreateTaskError, selectTimelineData, setTimelineData, updateTask, selectTaskList, selectUpdateTaskStatus, resetUpdateTaskFlags, paxCheckIn, selectPaxCheckInStatus, resetPaxCheckInFlags, selectPassengers, setBookingLocation } from "_redux/slices/timeline/timeline-data";
import { selectFirebaseInfo, selectFirebaseUser } from "_redux/slices/main-screen/user";
import { sendEmployeeNotification, selectEmployeeList, selectClickedEmployeeId, updateEmployee, } from "_redux/slices/timeline/employees";
import { selectSelectedTasksList, selectSelectedTask, setSelectedTaskList, setSelectedTask, selectSelectedMultiplePAXTask, setSelectedMultiplePAXTask, selectSelectedMultiplePAXTaskError, setSelectedMultiplePAXTaskError } from "_redux/slices/timeline/selected-task";
import { selectSelectedTimeline, selectDropOffLocations, selectPickUpLocations } from "_redux/slices/timeline/timeline-areas";
import { selectDefaultCheckInLocation } from "_redux/slices/timeline/timeline-control";
import { createFlightConversation, selectFlightListIndexed } from "_redux/slices/timeline/flight-information-list";
import { setToast, setShowToast } from "_redux/slices/timeline/timeline-toast";
import { selectUsedTheme } from "_redux/slices/timeline/theme";

// styling
import { Colors, Spacing } from "_styles";
import * as JobConfirmationStyles from "./job-confirmation-styles"
import { IoWarning } from "react-icons/io5";



// functions
import { convertShiftTimeStamp, getSeatNumber, processName, getThirtyMinuteInterval, isNormalTask, getEmployeeStatusForAppVersion } from "_utils/functions";
import BookingNotesSuggestions, { normalizeBookingNotes } from "_components/molecules/assistance-bookings/booking-notes-suggestions";

//#endregion

const JobConfirmation = (props) => {

  //#region redux
  const dispatch = useDispatch();

  const latestJob = useSelector(selectLatestJob);                               // latest job created from the timeline

  const createTaskStatus = useSelector(selectCreateTaskStatus);                 // status of the task creation
  const error = useSelector(selectCreateTaskError);                             // error message from task creation

  const taskList = useSelector(selectTaskList);                                 // time Line task list
  const selectedTasksList = useSelector(selectSelectedTasksList);               // selected tasks list from the job list        
  const selectedTask = useSelector(selectSelectedTask);                         // selected task from the job list
  const selectedMultiplePAXTask = useSelector(selectSelectedMultiplePAXTask);   // selected task with multiple pax from the job list
  const selectedMultiplePAXTaskError = useSelector(selectSelectedMultiplePAXTaskError);   // error message from the selected multiple pax task
  const selectedTimeline = useSelector(selectSelectedTimeline);                 // selected timeline from the timeline data
  const timelineData = useSelector(selectTimelineData);                         // timeline data object
  const updateTaskStatus = useSelector(selectUpdateTaskStatus);                 // update task status 

  const employeeList = useSelector(selectEmployeeList);                         // employee list from the timeline

  const dropOffLocations = useSelector(selectDropOffLocations);                 // airport drop off locations
  const pickUpLocations = useSelector(selectPickUpLocations);                   // airport pick up locations
  const paxCheckInStatus = useSelector(selectPaxCheckInStatus);                 // status of passenger check in 
  const defaultCheckInLocation = useSelector(selectDefaultCheckInLocation)      // default check in location from the timeline data

  const flightList = useSelector(selectFlightListIndexed);                      // list of flights
  const usedTheme = useSelector(selectUsedTheme);
  const clickedEmployeeId = useSelector(selectClickedEmployeeId)
  // airport
  const dbUser = useSelector(selectFirebaseInfo);
  const firebaseUser = useSelector(selectFirebaseUser);
  const iata = dbUser?.details?.authority;

  //#endregion

  //#region local states

  const [clicked, setClicked] = useState([]);                                     // if job is clicked or not
  const [selectedEmployee, setSelectedEmployee] = useState(null)                  // holds value of employee shift end time
  const [selectedDropOff, setSelectedDropOff] = useState(null)                    // drop off location of the PAX
  const [selectedPickUp, setSelectedPickUp] = useState(null)                      // pick up location of the PAX
  const [bookingNotes, setBookingNotes] = useState("")                            // view booking notes
  const [flightNumber, setFlightNumber] = useState("")                            // flight number of the PAX
  const [type, setType] = useState('arrival')                                     // type of the flight (arrival or departure)        
  const [airportIata, setAirportIata] = useState('')                              // airport iata code of the flight
  const [flightTime, setFlightTime] = useState('')                              // flight time of the PAX
  const [forMultiplePaxAssign, setForMultiplePaxAssign] = useState(false)             // hold value if the choice is to select multiple pax is turned on or off
  const [multiplePax, setMultiplePax] = useState("")                              // hold value of the multiple pax selected

  //#endregion

  //#region useEffect
  // on render clear / reset redux state
  useEffect(() => {
    dispatch(clearCreateTaskStatus());
    dispatch(resetUpdateTaskFlags());
  }, []);


  // auto set inital task in selected task list as the selected task for the job assignment
  useEffect(() => {
    // if there is no selected task and the selected task list is not empty array set the selected task to the first element in selectedTasksList array
    if (Object.isObjectEmpty(selectedTask) && Array.isFullArray(selectedTasksList)) {

      let tempSelectedTask = selectedTasksList?.[0] ?? null;

      // check task is not empty before setting it as the selected task + clicked task
      if (!Object.isObjectEmpty(tempSelectedTask)) {
        dispatch(setSelectedTask(tempSelectedTask));
        setClicked(tempSelectedTask?.["_id"] ?? null);
      }

    };
  }, []);

  // set booking notes
  useEffect(() => {
    // check if selected task exists
    if (Object.isObjectEmpty(selectedTask)) {
      setBookingNotes("")
      setType('')
      setAirportIata('')
      setFlightTime('')
      setFlightNumber('')

      return;
    };


    // set notes based on selected task booking notes value
    if (selectedTask?.flightBooking) {
      setBookingNotes(selectedTask?.originalBookingDoc?.notes ?? "")
    }

    if (!selectedTask?.flightBooking) {
      setBookingNotes(selectedTask?.notes ?? "")
    }

    if (selectedTask?.flightConfirmationDetails?.flightNumber && selectedTask?.flightConfirmationDetails?.carrierCode) {
      let carrierCode = selectedTask?.flightConfirmationDetails?.carrierCode ?? ""
      if (carrierCode && carrierCode == 'U2') carrierCode = 'EZY'
      setFlightNumber(carrierCode + selectedTask?.flightConfirmationDetails?.flightNumber ?? "")
    }
    //Handle task case to get details from FI property
    if (selectedTask.flightInformation) {
      let flightNumber = selectedTask.flightInformation.flightNumber;
      if (flightNumber.startsWith("U2")) {
        flightNumber = flightNumber.replace("U2", "EZY");
      }
      setFlightNumber(flightNumber);
      setAirportIata(iata)

    }

    if (selectedTask?.flightConfirmationDetails?.origin) {
      // check if departure or arrival
      if (selectedTask?.flightConfirmationDetails?.origin == iata) {
        // departure
        setType('departure')
        setAirportIata(selectedTask?.flightConfirmationDetails?.origin)

        // set flight time
        const date = new Date(selectedTask?.flightConfirmationDetails?.departureTime);
        const timeString = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
        setFlightTime(timeString)
      }
      else {
        // arrival
        setType('arrival')
        setAirportIata(selectedTask?.flightConfirmationDetails?.destination)

        // set flight time
        const date = new Date(selectedTask?.flightConfirmationDetails?.arrivalTime);
        const timeString = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
        setFlightTime(timeString)

      }
    }
  }, [selectedTask]);


  useEffect(() => {
    if (!Object.isObjectEmpty(selectedTask)) return;

    if (!forMultiplePaxAssign || !Array.isFullArray(selectedMultiplePAXTask)) {
      setBookingNotes("")
      setType('')
      setAirportIata('')
      setFlightTime('')
      setFlightNumber('')

      return;
    } else {
      const firstSelectedTask = Array.isFullArray(selectedMultiplePAXTask) ? selectedMultiplePAXTask?.[0] : null;
      if (firstSelectedTask) {
        setType(firstSelectedTask?.flightConfirmationDetails?.origin == iata ? 'departure' : 'arrival');
        setAirportIata(firstSelectedTask?.flightConfirmationDetails?.origin == iata
          ? firstSelectedTask?.flightConfirmationDetails?.destination
          : firstSelectedTask?.flightConfirmationDetails?.origin);
        const flightTime = firstSelectedTask?.flightConfirmationDetails?.origin == iata
          ? firstSelectedTask?.flightConfirmationDetails?.departureTime
          : firstSelectedTask?.flightConfirmationDetails?.arrivalTime ? firstSelectedTask?.flightConfirmationDetails?.arrivalTime : firstSelectedTask?.flightConfirmationDetails?.departureTime;
        const timeString = new Date(flightTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
        setFlightTime(timeString)
        setFlightNumber(firstSelectedTask?.flightConfirmationDetails?.carrierCode + firstSelectedTask?.flightConfirmationDetails?.flightNumber);

        setMultiplePax(selectedMultiplePAXTask?.map((item) => item?.name).join(", "));
      }
    }
  }, [selectedMultiplePAXTask, forMultiplePaxAssign, selectedTask])

  // MOVE TO BACKEND!!!!!!
  // send notification to employee if task is successfully created or updated
  useEffect(() => {
    if (createTaskStatus == "succeeded" || updateTaskStatus == "succeeded") {

      dispatch(setToast({
        message: 'Successfully allocated task',
        statusType: 'success',
      }));
      dispatch(setShowToast(true));

      // close modal etc.
      props.taskConfirmed();

      // find selected employee
      let selectedEmployee = null;
      selectedEmployee = employeeList.find((employee) => employee.employeeId === latestJob?.employeeId);

      // send notification to employee if fcm token exsists
      if (selectedEmployee?.FCMTokens) {
        let notification = {
          notificationTitle: "New Assigned Task at " + latestJob?.assignedTime,
          notificationText: `Please collect passenger ${latestJob?.name ? latestJob?.name : ""}`,
          fcmToken: selectedEmployee?.FCMTokens,
          iata
        };
        dispatch(sendEmployeeNotification(notification));
      }

      // reset flags
      resetPage();
    }
  }, [createTaskStatus, latestJob, updateTaskStatus]);

  // set selected employee based on clicked column
  useEffect(() => {
    if (props?.clickedColumn) {
      let employeeId = props?.clickedColumn?.split("-")?.[1]
      if (employeeId) setSelectedEmployee(employeeList.find((employee) => employee.employeeId === employeeId))
    }
  }, [props?.clickedColumn]);

  // reset pax checkin flags when paxCheckInStatus is succeeded or failed
  useEffect(() => {
    if (paxCheckInStatus == "succeeded" || paxCheckInStatus == "failed") {
      dispatch(resetPaxCheckInFlags());
    }
  }, [paxCheckInStatus]);

  //#endregion

  //#region functions
  const getEmployeeById = (employeeId) => {
    return employeeList?.find((employee) => {
      return employee?.employeeId === employeeId
        || employee?.uid === employeeId
        || employee?.userId === employeeId
        || employee?._id === employeeId;
    });
  };

  const getVersionedEmployeeStatus = (employeeId, status) => {
    const employee = getEmployeeById(employeeId) ?? selectedEmployee;
    return getEmployeeStatusForAppVersion(status, employee?.appVersionNumber);
  };

  const updateEmployeeStatusForAppVersion = (employeeId, status) => {
    const versionedStatus = getVersionedEmployeeStatus(employeeId, status);

    if (!versionedStatus) return;

    const empPayload = {
      userId: employeeId,
      body: {
        status: versionedStatus
      }
    };

    dispatch(updateEmployee(empPayload));
  };

  // add each confirmed task to the time line cache
  const addTimelineTask = (job, column) => {
    // find destination column by converting column into half hour interval
    let properColumn = getThirtyMinuteInterval(column);

    const destinationColumn = { ...timelineData.columns[properColumn] };

    // find list of jobs in destination column
    const destinationJobs = [...destinationColumn.jobs];

    // add job to list
    destinationJobs.push(job);

    // set old job list to new list
    destinationColumn.jobs = destinationJobs;

    // create new data object for timeline data
    const newTimelineData = {
      ...timelineData,
      columns: { ...timelineData.columns, [properColumn]: destinationColumn },
    };

    // update timeline
    dispatch(setTimelineData({ data: newTimelineData, job: job }));
  };

  // handle confirmation of task assignment and creating task in firestore
  const confirm = async () => {

    // get the selected task from the selected task list or the selected task
    const confirmedTask = selectedTask || selectedTasksList[0]

    // find employee id and column using clicked column
    let column = props?.clickedColumn?.split("-")[0];
    let employeeId = props?.clickedColumn?.split("-")[1];

    // create job object using selected task and column
    let job = {
      ...confirmedTask,
      assignedTime: column,
      employeeId: employeeId,
    };

    // add the task to the timeline
    addTimelineTask(job, column, employeeId);

    // if selected task isALTPickUp it already exists in the tasklist so it only needs to be updated 
    if (confirmedTask?.isALTPickUp) {

      // find the alt pickup task in the task list to update it
      let tempALTPickUp = taskList?.find((taskItem) => taskItem?._id === confirmedTask?._id);

      if (tempALTPickUp) {
        let taskUpdates = {
          id: tempALTPickUp._id,
          assignedTime: column,
          employeeId: employeeId,
          pickUpLocation: formatPickUpLocation(selectedPickUp),
          dropOffLocation: formatDropOffLocation(selectedDropOff),
          pending: false,
          notes: job.notes ?? bookingNotes,
          paxPositionInfo: {
            activity: "Assigned",
            locationId: formatPickUpLocation(selectedPickUp)?.id ?? "",
            locationName: formatPickUpLocation(selectedPickUp)?.name ?? "",
            time: new Date(),
            employeeId: employeeId ?? null,
            employeeName: employeeList?.find((e) => e?.employeeId === employeeId)?.title || employeeList?.find((e) => e?.employeeId === employeeId)?.name || null,
          },
          selectALTPickUpPaxStatus: "selected"
        }
        dispatch(updateTask(taskUpdates));
        if (clickedEmployeeId) updateEmployeeStatusForAppVersion(clickedEmployeeId, "available");

      };

    }
    // task is not a pick up task so create a new task in the backend
    else {

      // create object to be dispatched to backend
      let taskObject = {};

      taskObject = {
        active: false,
        completed: false,
        employeeId: job.employeeId,
        ...(clickedEmployeeId && { uid: clickedEmployeeId }),//auto-generated firebase, old account with custom employeeId, not valid to update firestore doc

        assignedTime: job.assignedTime,
        flightInformation: {
          arrivalAirport: job.flightConfirmationDetails?.destination,
          departureAirport: job.flightConfirmationDetails?.origin,
          flightNumber: `${job.flightConfirmationDetails?.carrierCode}${job.flightConfirmationDetails?.flightNumber}`,
          flightTime: job.flightConfirmationDetails?.destination == iata ? job.flightConfirmationDetails?.arrivalTime : job.flightConfirmationDetails?.departureTime,
          flightDate: job.flightConfirmationDetails.destination == iata ? job?.flightConfirmationDetails?.arrivalTime?.split("T")[0] : job?.flightConfirmationDetails?.departureTime?.split("T")[0],
          arrivalTime: job.flightConfirmationDetails?.arrivalTime ?? null,
          departureTime: job.flightConfirmationDetails?.departureTime ?? null,
          departureDate: job.flightConfirmationDetails?.departureDate ?? null,
          seatNumber: getSeatNumber(job),
        },
        passengerInformation: {
          assistance: job?.assistance ? job?.assistance : [],
          title: job?.name ? job?.name : "n/a",
        },
        flightBooking: job.flightBooking ? job.flightBooking : false,
        allocatedTime: new Date(),
        _id: `${job?._id}_IATA_${iata}`,
        cancelled: false,
        pending: false,
        selectedTimeline: selectedTimeline?._id ?? null,
        dropOffLocation: formatDropOffLocation(selectedDropOff),
        pickUpLocation: formatPickUpLocation(selectedPickUp),
        isTransitBooking: job?.isTransitBooking ? true : false,
        notPlanned: false,
        isEstimatedBooking: job?.isEstimatedBooking ? true : false,
        passengerDutTime: job?.passengerDutTime ? job?.passengerDutTime : null,
        combinedBooking: job?.combinedBooking ? true : false,
        passengers: Array.isFullArray(job?.passengers) ? job?.passengers?.map((passenger) => {
          return {
            assistance: passenger?.assistance ?? [],
            name: passenger?.originalBookingDoc?.name ?? passenger?.name ?? "n/a",
            originalBookingId: passenger?.originalBookingId ?? passenger?._id ?? null,
            bookingCreateDate: passenger?.originalBookingDoc?.dateCreated ?? passenger?.dateCreated ?? null,
          }
        }) : null,
        bookingCreateDate: job?.dateCreated ?? null,
        notes: job.notes ?? bookingNotes,
      };

      const bookingNoteEntries = normalizeBookingNotes(bookingNotes);
      if (bookingNoteEntries.length > 0 && iata !== "JER") {
        taskObject["taskNotes"] = bookingNoteEntries;
      }

      // check if job has flightBooking it's arrival or if it has isEstimatedBooking it Estimated departure add originalBookingId to the task for edit booking
      if (job?.flightBooking || job?.isEstimatedBooking) taskObject["originalBookingId"] = job?.originalBookingId ?? null;
      if (job?.flightBooking) taskObject["bookingCreateDate"] = job?.originalBookingDoc?.dateCreated ?? null;
      if (job?.combinedRampBooking) taskObject["combinedRampBooking"] = true;
      // if (!job?.combinedBooking && !job?.combinedRampBooking) taskObject["_id"] = `${job?._id}_IATA_${iata}`;

      // check if selectedTask has paxPositionInfo it's a checked in passenger booking add the checkInTime to task
      if (!Object.isObjectEmpty(confirmedTask?.paxPositionInfo)) { taskObject["checkInTime"] = confirmedTask?.paxPositionInfo?.time }

      // add gate information to departure flight
      if (job.gateInformation) {
        taskObject["flightInformation"] = {
          ...taskObject.flightInformation,
          gate: job.gateInformation.gate,
          stand: job.gateInformation.stand,
          terminalTitle: job.gateInformation.terminal,
        }
      }

      // add gate information to arrival flight
      if (job.flightBooking && job.originalBookingDoc && job.originalBookingDoc.gateInformation) {
        taskObject["flightInformation"] = {
          ...taskObject.flightInformation,
          gate: job.originalBookingDoc.gateInformation.gate,
          stand: job.originalBookingDoc.gateInformation.stand,
          terminalTitle: job.originalBookingDoc.gateInformation.terminal,
        }
      }

      // if task has no gate information
      if (!taskObject.flightInformation.gate) {
        // try to find the flight number in the flight list and add the gate information to the task object
        const flightNumber = `${job?.flightConfirmationDetails?.carrierCode}${job?.flightConfirmationDetails?.flightNumber}`;
        const flightDocument = flightList[`${flightNumber}_${job?.flightConfirmationDetails?.origin}_${job?.flightConfirmationDetails?.destination}`];
        if (flightDocument) {
          taskObject["flightInformation"] = {
            ...taskObject.flightInformation,
            gate: flightDocument.gate ?? "N/A",
            stand: flightDocument.stand ?? "N/A",
            terminalTitle: flightDocument.terminal ?? "N/A",
            flightTime: flightDocument?.operationInfo?.time ? flightDocument?.operationInfo?.time : taskObject?.flightInformation?.flightTime,

          }
        }
      }
      if (job.ambiliftRequired) taskObject["ambiliftRequired"] = true
      // save task to firestore
      const res = await dispatch(createTask(taskObject));

      // check response from createTask is successful change the paxStatus in paxPositionInfo to assigned
      if (res?.meta?.requestStatus === "fulfilled") {
        if (dbUser.details.authority !== "JER") {
          if (job?.combinedBooking && Array.isFullArray(job?.passengers)) {
            const payload = {
              taskId: confirmedTask?._id,
              body: {
                bookingsIds: job?.passengers?.map((pax) => pax?._id),
                taskType: "combinedBooking",
                toAssign: true,
                iata: dbUser.details.authority

              }

            };
            dispatch(selectPassengers(payload));
          }
          if (job?.combinedRampBooking && Array.isFullArray(job?.passengers)) {
            const payload = {
              taskId: confirmedTask?._id,
              body: {
                bookingsIds: job?.passengers?.map((pax) => pax?.originalBookingId),
                taskType: "combinedRampBooking",
                toAssign: true,
                iata: dbUser.details.authority

              }
            };
            dispatch(selectPassengers(payload));
          };


          let wantedFlight = null;
          if (!Object.isObjectEmpty(flightList)) {
            wantedFlight = flightList[`${job?.flightConfirmationDetails?.carrierCode}${job?.flightConfirmationDetails?.flightNumber}_${job?.flightConfirmationDetails?.origin}_${job?.flightConfirmationDetails?.destination}`]
          }

          // check if flight has landed or departed  based on time 
          let isFlightDisabled = false;
          if (wantedFlight) {
            const minusTime = wantedFlight?.type === "departure"
              ? 1000 * 60 * 5  // 5 minutes for departures
              : 1000 * 60 * 60; // 1 hour for arrivals
            const currentTime = new Date(new Date().getTime() - minusTime);
            const operationTime = new Date(wantedFlight?.operationInfo?.time);
            if (currentTime > operationTime) {
              isFlightDisabled = true;
            }
          } else {
            const flightDate = job?.flightConfirmationDetails?.origin == iata
              ? job?.flightConfirmationDetails?.departureDate
              : job?.flightConfirmationDetails?.arrivalTime ? job?.flightConfirmationDetails?.arrivalTime?.split("T")?.[0] : job?.flightConfirmationDetails?.departureDate
            const minusTime = job?.flightConfirmationDetails?.origin == iata
              ? 1000 * 60 * 5  // 5 minutes for departures
              : 1000 * 60 * 60; // 1 hour for arrivals
            const currentTime = new Date(new Date().getTime() - minusTime);
            const operationTime = new Date(flightDate);
            if (currentTime > operationTime) {
              isFlightDisabled = true;
            }
          }
          if (!isFlightDisabled) {
            let conversationPayload = null;
            if (!Object.isObjectEmpty(wantedFlight)) {
              conversationPayload = {
                iata: iata,
                body: {
                  flightId: wantedFlight?._id,
                  flightNumber: wantedFlight?.flightNumber,
                  createdBy: firebaseUser?.uid ?? "",
                  flightDate: wantedFlight?.operationInfo?.flightTime?.split("T")?.[0] ?? new Date()?.toISOString()?.split("T")?.[0],
                  participants: [
                    {
                      id: firebaseUser?.uid ?? "",
                      name: firebaseUser?.displayName ?? "",
                      type: "portal",
                    },
                    {
                      id: selectedEmployee?.employeeId ?? "",
                      name: selectedEmployee?.title ?? "",
                      type: "app",
                    }
                  ]
                }
              }

            } else {
              const flightDate = job?.flightConfirmationDetails?.origin == iata
                ? job?.flightConfirmationDetails?.departureDate
                : job?.flightConfirmationDetails?.arrivalTime ? job?.flightConfirmationDetails?.arrivalTime?.split("T")?.[0] : job?.flightConfirmationDetails?.departureDate
              const flightId = `${job?.flightConfirmationDetails?.carrierCode}${job?.flightConfirmationDetails?.flightNumber}_${job?.flightConfirmationDetails?.origin}_${job?.flightConfirmationDetails?.destination}_${flightDate ? flightDate : new Date()?.toISOString()?.split("T")?.[0]}_IATA_${iata}`;
              conversationPayload = {
                iata: iata,
                body: {
                  flightId: flightId,
                  flightNumber: `${job?.flightConfirmationDetails?.carrierCode}${job?.flightConfirmationDetails?.flightNumber}`,
                  createdBy: firebaseUser?.uid ?? "",
                  flightDate: flightDate,
                  participants: [
                    {
                      id: firebaseUser?.uid ?? "",
                      name: firebaseUser?.displayName ?? "",
                      type: "portal",
                    },
                    {
                      id: selectedEmployee?.employeeId ?? "",
                      name: selectedEmployee?.title ?? "",
                      type: "app",
                    }
                  ]
                }
              }
            }

            dispatch(createFlightConversation(conversationPayload))
          }

        }
        // if the job is departure and not combined and not duplicated 
        if (job.flightConfirmationDetails?.origin == iata && !job?.combinedBooking && !job?.combinedRampBooking && !job?._id?.includes("_part-") && !job?._id?.includes("-part")) {
          if (dbUser.details.authority !== "JER") {
            const payload = {
              taskId: confirmedTask?._id,
              body: {
                bookingsIds: [job?._id],
                taskType: "departure",
                toAssign: true,
                iata: dbUser.details.authority

              }

            };
            dispatch(selectPassengers(payload));
          }
        }
        if ((!Object.isObjectEmpty(confirmedTask?.paxPositionInfo) || selectedPickUp) && isNormalTask(confirmedTask)) {
          /* update booking location status */
          const assignedEmployee = employeeList?.find((e) => e?.employeeId === job?.employeeId);
          const employeeName = assignedEmployee?.title || assignedEmployee?.name || null;

          let object = {
            bookingId: confirmedTask.flightBooking ? confirmedTask.originalBookingId : confirmedTask?._id,
            location: confirmedTask?.paxPositionInfo?.locationId ? confirmedTask?.paxPositionInfo?.locationId : formatPickUpLocation(selectedPickUp)?.id,
            locationTitle: confirmedTask?.paxPositionInfo?.locationTitle ? confirmedTask?.paxPositionInfo?.locationTitle : formatPickUpLocation(selectedPickUp)?.name,
            activity: "Assigned",
            time: new Date(),
            checkInFlag: false,
            employeeId: job?.employeeId ?? null,
            employeeName,
            setBy: {
              id: firebaseUser?.uid ?? "",
              name: firebaseUser?.displayName ?? "",
              from: "portal",
              time: new Date(),
            }
          }

          dispatch(setBookingLocation(object))

        }
      }
    };

  };

  const handleMultiplePaxAssign = async () => {
    // get the selected task from the selected task list or the selected task
    const confirmedTask = selectedMultiplePAXTask?.[0]

    // find employee id and column using clicked column
    let column = props?.clickedColumn?.split("-")[0];
    let employeeId = props?.clickedColumn?.split("-")[1];

    let job = {
      ...confirmedTask,
      assignedTime: column,
      employeeId: employeeId,
    };

    let taskObject = {};
    const flightNumber = `${job?.flightConfirmationDetails?.carrierCode}${job?.flightConfirmationDetails?.flightNumber}`;
    const flightDate = job?.flightConfirmationDetails?.destination == iata && job?.flightConfirmationDetails?.arrivalTime
      ? job?.flightConfirmationDetails?.arrivalTime?.split("T")?.[0]
      : job?.flightConfirmationDetails?.departureTime
        ? job?.flightConfirmationDetails?.departureTime?.split("T")?.[0]
        : job?.flightConfirmationDetails?.departureDate;
    taskObject = {
      active: false,
      completed: false,
      employeeId: job.employeeId,
      assignedTime: job.assignedTime,
      flightInformation: {
        arrivalAirport: job?.flightConfirmationDetails?.destination,
        departureAirport: job?.flightConfirmationDetails?.origin,
        flightNumber: flightNumber,
        flightTime: job?.flightConfirmationDetails?.destination == iata ? job?.flightConfirmationDetails?.arrivalTime : job?.flightConfirmationDetails?.departureTime,
        flightDate: flightDate,
        arrivalTime: job?.flightConfirmationDetails?.arrivalTime ?? null,
        departureTime: job?.flightConfirmationDetails?.departureTime ?? null,
        departureDate: job?.flightConfirmationDetails?.departureDate ?? null,
        // seatNumber: getSeatNumber(job),
      },
      passengerInformation: {
        assistance: Array.isFullArray(selectedMultiplePAXTask) && selectedMultiplePAXTask?.length > 1 ? [] : job?.assistance ?? [],
        title: Array.isFullArray(selectedMultiplePAXTask) && selectedMultiplePAXTask?.length > 1 ? "" : job?.name ?? "",
      },
      flightBooking: job?.flightBooking ? job?.flightBooking : false,
      allocatedTime: new Date(),
      _id: Array.isFullArray(selectedMultiplePAXTask) && selectedMultiplePAXTask?.length > 1
        ? `${flightNumber}${flightDate}_${new Date().getHours()}-${new Date().getMinutes()}-${new Date().getSeconds()}_IATA_${iata}`
        : `${job?._id}_IATA_${iata}`,
      cancelled: false,
      pending: false,
      selectedTimeline: selectedTimeline?._id ?? null,
      dropOffLocation: formatDropOffLocation(selectedDropOff),
      pickUpLocation: formatPickUpLocation(selectedPickUp),
      isTransitBooking: job?.isTransitBooking ? true : false,
      notPlanned: false,
      isEstimatedBooking: job?.isEstimatedBooking ? true : false,
      passengerDutTime: null,
      combinedBooking: false,
      passengers: Array.isFullArray(selectedMultiplePAXTask) && selectedMultiplePAXTask?.length > 1 ? selectedMultiplePAXTask?.map((passenger) => {
        return {
          assistance: passenger?.assistance ?? [],
          name: passenger?.originalBookingDoc?.name ?? passenger?.name ?? "n/a",
          originalBookingId: passenger?.originalBookingId ?? passenger?._id ?? null,
          bookingCreateDate: passenger?.originalBookingDoc?.dateCreated ?? passenger?.dateCreated ?? null,
        }
      }) : null,
      bookingCreateDate: job?.dateCreated ?? null,
      notes: job.notes ?? bookingNotes,
      groupedBooking: Array.isFullArray(selectedMultiplePAXTask) && selectedMultiplePAXTask?.length > 1 ? true : false,
    };
    if (job?.flightBooking) taskObject["bookingCreateDate"] = job?.originalBookingDoc?.dateCreated ?? null;
    // check if selectedTask has paxPositionInfo it's a checked in passenger booking add the checkInTime to task
    if (!Object.isObjectEmpty(confirmedTask?.paxPositionInfo)) { taskObject["checkInTime"] = confirmedTask?.paxPositionInfo?.time }
    const bookingNoteEntries = normalizeBookingNotes(bookingNotes);
    if (bookingNoteEntries.length > 0) {
      taskObject["taskNotes"] = bookingNoteEntries;
    }

    dispatch(setTimelineData({ data: null, job: taskObject }));
    if (job.ambiliftRequired) taskObject["ambiliftRequired"] = true

    // save task to firestore
    const res = await dispatch(createTask(taskObject));

    // check response from createTask is successful change the paxStatus in paxPositionInfo to assigned
    if (res?.meta?.requestStatus === "fulfilled") {
      // if the job is departure and not combined and not duplicated 
      if (!job?.combinedBooking && !job?.combinedRampBooking && !job?._id?.includes("_part-") && !job?._id?.includes("-part")) {

        const payload = {
          taskId: taskObject?._id,
          body: {
            bookingsIds: taskObject?.passengers?.map((pax) => pax?.originalBookingId) ?? [job?._id],
            taskType: taskObject?.flightBooking ? "arrival" : "departure",
            toAssign: true,
            iata: dbUser.details.authority
          }
        };
        dispatch(selectPassengers(payload));
      }
    }
  }

  // handle clearing page, states and selected task list
  const resetPage = () => {

    // clear flags
    dispatch(clearCreateTaskStatus());
    dispatch(resetUpdateTaskFlags());

    // remove the created task from selected task list that needs to be assigned
    if (latestJob?.groupedBooking && latestJob?.passengers?.length > 1) {
      const finalSelectedTasksList = Array.isFullArray(selectedTasksList) ? selectedTasksList?.filter((item) => latestJob?.passengers?.map((pax) => !pax?.originalBookingId)?.includes(item?._id)) : [];
      dispatch(setSelectedTaskList(finalSelectedTasksList));
    } else {
      const finalSelectedTasksList = Array.isFullArray(selectedTasksList) ? selectedTasksList?.filter((item) => item?._id != latestJob?._id) : [];
      dispatch(setSelectedTaskList(finalSelectedTasksList));
    }

    // reset selected column
    props?.setClickedColumn(false);
  };

  const reset = () => {
    // need to reset data back to original state   
    props.taskRejected();
    dispatch(setSelectedTaskList([]));
  };

  // format drop off location to result object with location id and location name 
  const formatDropOffLocation = (id) => {
    if (!id) return null;
    const location = dropOffLocations.find((location) => location._id === id);
    if (location) return { id: location._id, name: location.title };
    else return null
  }

  // format pick up location to result object with location id and location name 
  const formatPickUpLocation = (id) => {
    if (!id) return null;
    const location = pickUpLocations.find((location) => location._id === id);
    if (location) return { id: location._id, name: location.title };
    else return null
  }

  const handleSwitchSelectMultiplePax = () => {
    setForMultiplePaxAssign(!forMultiplePaxAssign);
    setClicked([]);
    dispatch(setSelectedTask(null));
    dispatch(setSelectedMultiplePAXTask([]));
    dispatch(setSelectedMultiplePAXTaskError(null));
  }
  function getPaxName(doc) {
    if (!doc) return ""
    if (doc.name) return processName(doc.name);
    if (doc.combinedBooking) return "Boarding task";
    if (doc.combinedRampBooking) return "Ramp task";
    if (doc.passengerInformation?.title && doc.passengerInformation?.title != "N/A") return processName(doc.passengerInformation?.title)
    return "N/A"
  }

  function getPassengerName(task) {
    let name = "N/A"
    if (!task) return name
    if (task.name) name = processName(task.name);
    if (task.combinedBooking) name = `Boarding ${selectedTask?.passengers.length ?? 0} pax`;
    if (task.combinedRampBooking) name = `Ramp ${selectedTask?.passengers.length ?? 0} pax`;
    if (forMultiplePaxAssign) name = multiplePax;
    return name
  }

  //#endregion

  return (
    <div>

      {/* create task modal body */}
      <div style={{
        opacity: createTaskStatus == "loading" || updateTaskStatus == "loading" ? 0.2 : 1.0,
        flex: 1,
        opacity: createTaskStatus == "loading" || updateTaskStatus == "loading" ? 0.2 : 1.0,
      }}>

        {/* title + task list + employee info text */}
        <div style={{ flex: 5 }}>

          {/* map job card list */}
          {Array.isFullArray(selectedTasksList) && (
            <div>
              <Subtitle>Selected PRM Booking</Subtitle>
              {selectedTasksList?.length > 1 && iata !== "JER" && <div style={{ display: 'flex', alignItems: 'center', gap: Spacing.SCALE_10, marginBottom: Spacing.SCALE_10, marginTop: Spacing.SCALE_10 }}>
                <p className="mb-0">Turn on to select multiple PAX and assign them</p>
                <Form>
                  <Form.Check
                    onChange={handleSwitchSelectMultiplePax}
                    type="switch"
                    id="custom-switch"
                    checked={forMultiplePaxAssign}
                  />
                </Form>
              </div>}
              {selectedMultiplePAXTaskError && <p className="mb-0" style={{ color: 'red' }}>{selectedMultiplePAXTaskError}</p>}
              <p>
                You have selected to assign{" "}
                <span style={{ color: Colors.PRM_LIGHT_BLUE }}>
                  {getPaxName(selectedTask)}
                </span>{" "}
                on{" "}
                <span style={{ color: Colors.PRM_LIGHT_BLUE }}>
                  {flightNumber} ({airportIata})
                </span>{" "}
                at{" "}
                <span style={{ color: Colors.PRM_LIGHT_BLUE }}>
                  {flightTime}
                </span>{" "}
                to{" "}
                <span style={{ color: Colors.PRM_LIGHT_BLUE }}>
                  {selectedEmployee ? selectedEmployee?.title : ""}
                </span>{" "}
                at{" "}
                <span style={{ color: Colors.PRM_LIGHT_BLUE }}>
                  {props?.clickedColumn ? props?.clickedColumn?.split("-")[0] : ""}
                </span>
              </p>
              <div style={{ ...JobConfirmationStyles.taskListContainer, justifyContent: "flex-start", alignItems: 'flex-start' }} >
                <div className='mt-2' style={{ display: 'flex', justifyContent: "flex-start", alignItems: 'flex-start', marginBottom: Spacing.SCALE_12 }}>
                  {selectedTasksList?.map((task, index) => (
                    <div key={index}
                      style={{
                        marginLeft: index == 0 ? 0 : Spacing.SCALE_12,
                        borderWidth: 2.5, borderStyle: 'solid', borderRadius: 10,
                        borderColor: task?.['_id'] == selectedTask?.['_id'] || (forMultiplePaxAssign && selectedMultiplePAXTask?.map((item) => item?._id)?.includes(task?._id)) ? Colors.PRM_LIGHT_BLUE : 'white'
                      }}>
                      <JobCard
                        job={task}
                        index={index}
                        clicked={clicked || (forMultiplePaxAssign && selectedMultiplePAXTask?.includes(task?._id))}
                        setClicked={setClicked}
                        toConfirm={true}
                        jobConfirmation={true}      // prop to show the job confirmation modal  
                        forMultiplePaxAssign={forMultiplePaxAssign}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* task + employee info + warning message + error message */}
          <div className='mt-3'>

            {/* pax name, employee name, employee id, assigned time */}
            <div style={{ display: "flex", flex: 1, justifyContent: "space-between" }}>

              {/* selected pax name */}
              <div style={{ flex: 1 }}>
                <div>
                  <Subtitle>{selectedTask?.combinedBooking || selectedTask?.combinedRampBooking ? "Passengers" : "Passenger"}</Subtitle>
                  <p>{getPassengerName(selectedTask)}</p>
                </div>
              </div>

              {/* assigned employee name */}
              <div style={{ flex: 1 }}>
                <Subtitle>Employee Name</Subtitle>
                <p>{selectedEmployee ? selectedEmployee?.title : ""}</p>
              </div>

              {/* flight time */}
              <div style={{ flex: 1 }}>
                <Subtitle>{type == 'departure' ? 'Departure Time' : 'Arrival Time'}</Subtitle>
                <p>{flightTime}</p>
              </div>

              {/* assigned time */}
              <div style={{ flex: 1, }}>
                <Subtitle>Assigned Time</Subtitle>
                <p>{props?.clickedColumn ? props?.clickedColumn?.split("-")[0] : ""}</p>
              </div>

            </div>

            {/* pick up Location, drop off location and booking notes */}
            <div className="mt-3">
              <PickupDropOffDropDowns
                selectedPickUp={selectedPickUp}
                selectedDropOff={selectedDropOff}
                setSelectedPickUp={(val) => { setSelectedPickUp(val) }}
                setSelectedDropOff={(val) => { setSelectedDropOff(val) }}
              />
            </div>

            {/* booking notes */}
            <Row className='mt-4'>
              <BookingNotesSuggestions setBookingNotes={setBookingNotes} bookingNotes={bookingNotes} />
            </Row>

            {/* create task error message */}
            <p>{error ? error : ""}</p>

          </div>

        </div>

        {/* confirm / reset buttons */}
        <div className='mt-5' style={{ display: "flex", justifyContent: "space-between", flex: 1 }}>
          <Button variant="danger" onClick={reset} >{"Reset Assignment"}</Button>
          <Button variant="primary" onClick={() => forMultiplePaxAssign ? handleMultiplePaxAssign() : confirm()} disabled={(!selectedTask && !forMultiplePaxAssign) || (forMultiplePaxAssign && !Array.isFullArray(selectedMultiplePAXTask))}>
            {selectedEmployee?.title ? "Assign Task to " + selectedEmployee?.title : "Confirm Task Assignment"}
          </Button>
        </div>

      </div>

      {/* loading spinner */}
      {createTaskStatus == "loading" || updateTaskStatus == "loading" && (
        <div
          style={{
            position: 'absolute',   // makes the spinner overlay the content
            top: '30%',             // vertically center
            left: '50%',            // horizontally center
            transform: 'translate(-50%, -50%)',  // exact centering
            zIndex: 9999            // makes sure it's on top of other content
          }}>
          <CustomSpinner rowCenter={true} />
        </div>
      )}
    </div>

  );
};

export default JobConfirmation;
